import { useEffect } from 'react';
import { ArrowRightIcon, ExternalLinkIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { business, images, interiorHeroes, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const hero = interiorHeroes.bigGreenEgg;
const TITLE = 'Big Green Egg Grills in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'Grill, smoke, roast and bake on a Big Green Egg. See the EGG and its accessories at Pegasus Spas in Brooklyn, Michigan.';
const CANONICAL = 'https://www.pegasus-spas.com/grills.html';
type Dish = {
  mode: string;
  dish: string;
  caption: string;
  image: string;
  alt: string;
  focal: string;
  recipe: string;
};
const dishes: Dish[] = [{
  mode: 'Grill',
  dish: 'The Perfect Burger',
  caption: 'A hot, direct fire, a good sear and a juicy middle. It\u2019s a Friday-night classic.',
  image: 'https://biggreenegg.com/cdn/shop/articles/20241209171136-the-perfect-hamburger_451bb081-35c3-478f-8fe9-07d42cc0c1f3.jpg?v=1770234176',
  alt: 'A thick grilled cheeseburger on a bun',
  focal: '50% 50%',
  recipe: 'https://biggreenegg.com/blogs/recipes/the-perfect-burger/'
}, {
  mode: 'Smoke',
  dish: 'Baby Back Ribs',
  caption: 'Low and slow over lump charcoal until the ribs pull clean off the bone.',
  image: 'https://biggreenegg.com/cdn/shop/articles/20241119144859-full-20u-202o6b8997-600x400-9801ebf_4fe5c62a-f4e3-4f94-91a3-07ad9dee6c90.png?v=1779201622',
  alt: 'Baby back ribs smoking on a Big Green Egg',
  focal: '50% 50%',
  recipe: 'https://biggreenegg.com/blogs/recipes/baby-back-ribs'
}, {
  mode: 'Roast',
  dish: 'Lemon & Garlic Roast Chicken',
  caption: 'Crisp golden skin, bright lemon and garlic. Sunday dinner, cooked outside.',
  image: 'https://biggreenegg.com/cdn/shop/articles/20241223172204-lemon-chicken-1200-1024x427.jpg?v=1780002088',
  alt: 'A golden roast chicken with lemon halves and garlic',
  focal: '50% 50%',
  recipe: 'https://biggreenegg.com/blogs/recipes/roast-chicken-with-lemon-and-garlic'
}, {
  mode: 'Bake',
  dish: 'Margherita Pizza',
  caption: 'A blistered crust, fresh mozzarella and basil, baked like a stone oven.',
  image: 'https://biggreenegg.com/cdn/shop/articles/20250305195214-margherita-20pizza_030525_947de5b7-2e31-4d29-ad9b-539940cc1951.jpg?v=1741204862',
  alt: 'A Margherita pizza with melted mozzarella and fresh basil',
  focal: '50% 50%',
  recipe: 'https://biggreenegg.com/blogs/recipes/margherita-pizza'
}];
const relatedLinks = [{
  label: 'Fire Tables',
  href: routes.fireTables
}, {
  label: 'Patio Umbrellas',
  href: routes.umbrellas
}, {
  label: 'Outdoor Furniture',
  href: routes.furniture
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function BigGreenEggPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-750" data-mp="2f46d2guq9qfm">
      {/* Intro band */}
      <section aria-labelledby="bge-h1" className="relative w-full" data-id="element-751" data-mp="2a6mz7i1xzfo7">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-752" data-mp="2a6wo12b93gsb">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-753" data-mp="258v4kg9wxfgb" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-754" data-mp="3gzxyf2ik14y" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-755" data-mp="2x8ogj2zcgoh2" />

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-756" data-mp="3198do6jx3s37">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-757" data-mp="2s8trl4z4q8yn">
              Outdoor Cooking
            </p>
            <h1 id="bge-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-758" data-mp="2dje2ruj5sdll">
              Big Green Egg Grills at Pegasus
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-759" data-mp="24hem53c23bj6">
              Burgers tonight, ribs on Saturday, pizza with the kids on Sunday. One ceramic cooker
              does all of it, and we&rsquo;d love to show you how.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-760" data-mp="1zj39ylyagoxs">
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-761" data-mp="1fy326dte49ba">
                See It at the Showroom
              </SiteLink>
              <SiteLink to={routes.bigGreenEggAccessories} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-762" data-mp="1kwxxgf8jnqca">
                Browse Accessories
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Food-led panels */}
      <section aria-labelledby="cook-heading" className="w-full bg-ink" data-id="element-763" data-mp="2iky7x27mzyvl">
        <div className="mx-auto max-w-container px-5 pb-8 pt-12 sm:px-8 lg:pt-14" data-id="element-764" data-mp="2ij3k9g4zdsnt">
          <h2 id="cook-heading" className="max-w-3xl font-display text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-765" data-mp="2dka6dmls7zn4">
            What Will You Cook First?
          </h2>
          <p className="mt-3 max-w-2xl text-body text-white/80 sm:text-body-lg" data-id="element-766" data-mp="2pmbfs0l2baqo">
            Grill, smoke, roast and bake, all on the same EGG.
          </p>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2" data-id="element-767" data-mp="2zg267b0r1v90">
          {dishes.map(dish => <li key={dish.mode} className="relative" data-id="element-768" data-mp="34d0uhcxqf7za">
              <a href={dish.recipe} target="_blank" rel="noopener noreferrer" aria-label={`${dish.mode}: ${dish.dish} recipe from Big Green Egg (opens in a new tab)`} className="group relative block w-full overflow-hidden focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-white sm:aspect-[4/3] lg:aspect-[16/10]" data-id="element-769" data-mp="38emgpow5oym5">
                <img src={dish.image} alt={dish.alt} className="block aspect-[4/3] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100 sm:absolute sm:inset-0 sm:h-full" style={{
              objectPosition: dish.focal
            }} loading="lazy" decoding="async" data-id="element-770" data-mp="1o1erkgd4n4m8" />
                <div className="relative bg-ink px-5 py-4 sm:absolute sm:inset-x-0 sm:bottom-0 sm:bg-ink/80 sm:px-6 sm:py-5" data-id="element-771" data-mp="1zdcf3u8058eg">
                  <p className="text-[14px] font-bold uppercase text-ember" data-id="element-772" data-mp="1ugcbrr2jejr1">{dish.mode}</p>
                  <h3 className="mt-1 font-display text-[22px] font-semibold leading-7 text-white sm:text-[26px] sm:leading-8" data-id="element-773" data-mp="1pemv9vwjpdvk">
                    {dish.dish}
                  </h3>
                  <p className="mt-1 text-[16px] leading-6 text-white/85" data-id="element-774" data-mp="e23w8gg6paow">{dish.caption}</p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[15.5px] font-bold text-white underline decoration-white/50 underline-offset-4 group-hover:decoration-white" data-id="element-775" data-mp="916f876wumoz">
                    Get the recipe
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-776" data-mp="13qrhr2asejns" />
                  </span>
                </div>
              </a>
            </li>)}
        </ul>

        <div className="mx-auto max-w-container px-5 py-4 sm:px-8" data-id="element-777" data-mp="ts20nbqpt38l">
          <p className="text-[14px] leading-5 text-white/60" data-id="element-778" data-mp="1hgcv9x5ti6of">
            Food photography and recipes courtesy of Big Green Egg.
          </p>
        </div>
      </section>

      {/* Showroom CTA */}
      <section aria-labelledby="local-heading" className="w-full bg-white" data-id="element-779" data-mp="18p2t5hgqd284">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-780" data-mp="1cq4sdyandea3">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-781" data-mp="1co1s8lfs64rn">
            <div className="lg:col-span-6" data-id="element-782" data-mp="20efoyp9b8rsk">
              <h2 id="local-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-783" data-mp="256z25l435kwu">
                Come Lift the Lid
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-784" data-mp="3kwq8iiypwjus">
                Stop by the Brooklyn showroom to see the EGG up close. Tell us what you like to
                cook and we&rsquo;ll help you pick the size and accessories to get started.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-785" data-mp="36xgrxi6lnnqa">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-786" data-mp="36ztfcav4mqaq">
                  <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-787" data-mp="w5xe7kjvp41e" />
                  Visit the Showroom
                </SiteLink>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-788" data-mp="r7nkzfhpsdif">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-789" data-mp="yqptr47smbhm" />
                  Call or Text {business.phone}
                </a>
              </div>
              <SiteLink to={routes.bigGreenEggAccessories} className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-790" data-mp="otuwspr64j5g">
                Browse Big Green Egg Accessories
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-791" data-mp="2a7pixqyuga7f" />
              </SiteLink>
            </div>

            <div className="lg:col-span-6" data-id="element-792" data-mp="2594j3sni7sji">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-793" data-mp="25604s2gbrgf4">
                <img src={images.backyardLiving} alt="Friends relaxing around a fire table beside a Big Green Egg on a backyard deck" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-794" data-mp="2cr2mrgj089et" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-795" data-mp="1yruocpuo15bo">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-796" data-mp="23qxhpd1a9n3t">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-797" data-mp="23pmdjc3xedc6">
            {relatedLinks.map(link => <li key={link.label} data-id="element-798" data-mp="3f6cnn6bgzpb2">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-799" data-mp="3f5b042d9pacg">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-800" data-mp="1t3w2t9kexuxz" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}